import React, { useEffect, useMemo, useState } from "react";
import { MapPin, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PatientAppLayout from "../../../../components/PatientAppLayout/PatientAppLayout";
import { apiRequest } from "../../../../Api/Api";
import { appointmentMapsHref, appointmentTelHref } from "../../../../utils/appointmentVenueLinks";
import {
  displayProviderName,
  normalizePatientAppointments,
} from "../../../../utils/patientAppointmentsNormalize";
import "./Appointment.scss";

const formatDate = (value) => {
  if (!value) return "Date pending";
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return "Date pending";
  return d.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const statusLabel = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "scheduled") return "Scheduled";
  if (key === "completed") return "Completed";
  if (key === "cancelled") return "Cancelled";
  return "Pending admin approval";
};

const statusTone = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "completed") return "isDone";
  if (key === "scheduled") return "isScheduled";
  if (key === "cancelled") return "isCancelled";
  return "isPending";
};

const fmtINR = (n) => `Rs ${Number(n || 0).toLocaleString("en-IN")}`;

function hasVisitOutcome(item) {
  const t = item?.visitOutcomeAt;
  return t != null && String(t).trim() !== "";
}

function visitOutcomeLines(item) {
  if (!hasVisitOutcome(item)) return [];
  if (item.visitPatientAttended === false) {
    return ["Your provider recorded that you did not attend this scheduled visit."];
  }
  const lines = [];
  if (item.visitPatientAttended === true) {
    lines.push("Your provider confirmed you attended.");
    if (item.visitProcedureProceeding === true) {
      lines.push("The procedure is proceeding as planned.");
      if (item.visitSamePriceAsBooked === true && item.visitBookedPriceInr != null) {
        lines.push(`Agreed price remains ${fmtINR(item.visitBookedPriceInr)}.`);
      } else if (item.visitSamePriceAsBooked === false && item.visitFinalAgreedPriceInr != null) {
        lines.push(`Updated agreed amount: ${fmtINR(item.visitFinalAgreedPriceInr)}.`);
        if (String(item.visitPriceChangeReason || "").trim()) {
          lines.push(`Why it changed: ${String(item.visitPriceChangeReason).trim()}`);
        }
      }
    } else if (item.visitProcedureProceeding === false) {
      lines.push("Your provider indicated the procedure is not proceeding as originally planned for this visit.");
    }
  }
  return lines.length ? lines : ["Your provider added a visit update — open details for more."];
}

export default function Appointment() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [appointments, setAppointments] = useState([]);

  const loadAppointments = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiRequest({ method: "GET", url: "/app/appointments" });
      setAppointments(normalizePatientAppointments(res));
    } catch (err) {
      setError(err?.message || "Could not load appointments.");
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAppointments();
  }, []);

  useEffect(() => {
    if (loading || error) return;
    if (appointments.length === 0) {
      navigate("/HealBidDashboard", { replace: true });
    }
  }, [loading, error, appointments.length, navigate]);

  const grouped = useMemo(() => {
    const scheduled = [];
    const pending = [];
    const completed = [];
    appointments.forEach((item) => {
      const st = String(item.status || "").toLowerCase();
      if (st === "completed") completed.push(item);
      else if (st === "scheduled") scheduled.push(item);
      else pending.push(item);
    });
    return { scheduled, pending, completed };
  }, [appointments]);

  if (!loading && !error && appointments.length === 0) {
    return (
      <PatientAppLayout activeNav="dashboard" backTo="/HealBidDashboard" backLabel="Dashboard">
        <div className="apPage apPage--patientShell apPage--inShellLayout">
          <div className="apMain">
            <p className="apStateMsg apStateMsg--center">Taking you to your dashboard…</p>
          </div>
        </div>
      </PatientAppLayout>
    );
  }

  return (
    <PatientAppLayout activeNav="dashboard" backTo="/HealBidDashboard" backLabel="Dashboard">
      <div className="apPage apPage--patientShell apPage--inShellLayout">
        <div className="apMain">
          <section className="apSheet" aria-labelledby="ap-section-title">
            <header className="apSheet__head">
              <h2 id="ap-section-title" className="apSheet__title">
                Your appointment <span className="apSheet__titleAccent">journey</span>
              </h2>
              <p className="apSheet__lead">
                Track pending, scheduled, and completed appointments. Booking starts in the bid chat; this page shows
                approval status and visit details.
              </p>
            </header>

            <div className="apKpiRow" role="list" aria-label="Appointment counts">
              <div className="apKpiCard" role="listitem">
                <p className="apKpiCard__label">Pending approval</p>
                <strong className="apKpiCard__value">{grouped.pending.length}</strong>
              </div>
              <div className="apKpiCard" role="listitem">
                <p className="apKpiCard__label">Scheduled</p>
                <strong className="apKpiCard__value">{grouped.scheduled.length}</strong>
              </div>
              <div className="apKpiCard" role="listitem">
                <p className="apKpiCard__label">Completed</p>
                <strong className="apKpiCard__value">{grouped.completed.length}</strong>
              </div>
            </div>

            {loading ? <p className="apStateMsg">Loading appointments…</p> : null}
            {!loading && error ? <p className="apStateMsg apStateMsg--error">{error}</p> : null}

            {!loading && !error && appointments.length > 0 ? (
              <ul className="apList">
                {appointments.map((item) => {
                  const metaParts = [
                    formatDate(item.appointmentDate),
                    item.timeSlot || null,
                    item.clinicName || null,
                  ].filter(Boolean);
                  const mapsUrl = appointmentMapsHref(item);
                  const telUrl = appointmentTelHref(item.clinicPhone);
                  return (
                    <li key={item.id}>
                      <article className="apRow">
                        <div className="apRowMain">
                          <p className="apProviderName">{displayProviderName(item.providerRole, item.providerName)}</p>
                          <p className="apMeta">{metaParts.join(" · ")}</p>
                          {mapsUrl || telUrl ? (
                            <div className="apVenueActions">
                              {mapsUrl ? (
                                <a
                                  className="apVenueLink"
                                  href={mapsUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <MapPin size={14} strokeWidth={2.25} aria-hidden />
                                  See location
                                </a>
                              ) : null}
                              {telUrl ? (
                                <a className="apVenueLink" href={telUrl}>
                                  <Phone size={14} strokeWidth={2.25} aria-hidden />
                                  Call clinic
                                </a>
                              ) : null}
                            </div>
                          ) : null}
                          {item.noteForDoctor ? <p className="apNote">Note: {item.noteForDoctor}</p> : null}
                          {hasVisitOutcome(item) ? (
                            <div className="apVisitOutcome" role="status">
                              <p className="apVisitOutcome__label">From your provider after the visit</p>
                              <ul className="apVisitOutcome__list">
                                {visitOutcomeLines(item).map((line, i) => (
                                  <li key={`vo-${item.id}-${i}`}>{line}</li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                        </div>
                        <div className="apRowActions">
                          <span className={`apStatusBadge ${statusTone(item.status)}`}>
                            {statusLabel(item.status)}
                          </span>
                          <button
                            type="button"
                            className="apDetailsBtn"
                            onClick={() => navigate("/appointment-confirmed", { state: { appointment: item } })}
                          >
                            View details
                          </button>
                        </div>
                      </article>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </section>
        </div>
      </div>
    </PatientAppLayout>
  );
}
